<template>
  <div class="container">
    <div class="header">
      <div class="header-title" />
      <div class="search-section">
        <div class="search-container">
          <b-form-input
            v-model="searchQuery"
            class="search-input"
            placeholder="Rechercher un colis par numéro BL, téléphone ou nom du destinataire..."
            aria-label="Recherche logistique de colis"
            @keyup.enter="searchOrder"
          />
          <b-button
            variant="primary"
            class="search-btn"
            :disabled="loading"
            @click="searchOrder"
          >
            <b-spinner
              v-if="loading"
              small
            />
            <feather-icon
              v-else
              icon="SearchIcon"
              size="18"
            />
            {{ loading ? 'Recherche...' : 'Rechercher le colis' }}
          </b-button>
        </div>
      </div>
    </div>

    <b-alert
      v-if="error"
      variant="danger"
      show
      class="error-message"
    >
      <feather-icon
        icon="AlertTriangleIcon"
        size="18"
      />
      {{ error }}
    </b-alert>

    <div
      v-if="orderResults.length && !loading"
      class="main-content"
    >
      <div
        v-if="searchMeta"
        class="results-summary"
      >
        <feather-icon
          icon="ListIcon"
          size="18"
        />
        <span>
          {{ searchMeta.total_orders_found }} commande(s) trouvée(s)
          <template v-if="searchMeta.search_type === 'phone'">
            pour le téléphone {{ searchMeta.searched_value }}
          </template>
        </span>
      </div>

      <div
        v-for="orderData in orderResults"
        :key="orderData.id"
        class="order-result"
      >
        <b-alert
          v-if="orderData.is_deleted"
          variant="danger"
          show
          class="mb-2"
        >
          <feather-icon
            icon="TrashIcon"
            size="18"
            class="mr-1"
          />
          Colis supprimé
          <span v-if="orderData.deleted_at"> le {{ formatDateTime(orderData.deleted_at) }}</span>
          <span v-if="orderData.deleted_by_username"> par {{ orderData.deleted_by_username }}</span>.
          Visible uniquement pour les administrateurs.
        </b-alert>
        <b-tabs
          class="order-tabs"
          content-class="order-tabs-content"
          nav-class="order-tabs-nav"
          lazy
        >
          <b-tab
            active
            title="Détails du colis"
          >
            <!-- Combined Order and Client Information Section -->
            <div class="client-info-section">
        <div class="section-header">
          <div class="section-title">
            <feather-icon
              icon="PackageIcon"
              size="24"
            />
            <h2>Détails de la commande {{ orderData.id }}</h2>
          </div>
          <div class="header-right">
            <div class="info-card modern status-card">
              <div class="info-icon gradient-status">
                <feather-icon
                  icon="PackageIcon"
                  size="24"
                />
              </div>
              <div class="info-content">
                <div class="info-label">
                  Statut
                </div>
                <div class="info-value">
                  {{ orderData.current_status_display }}
                </div>
              </div>
            </div>
            <div class="info-card modern bl-card">
              <div class="info-icon gradient-bl">
                <feather-icon
                  icon="HashIcon"
                  size="24"
                />
              </div>
              <div class="info-content">
                <div class="info-label">
                  Numéro BL
                </div>
                <div class="info-value">
                  {{ orderData.id }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="client-info-grid">
          <div class="detail-card modern">
            <div class="detail-icon gradient-indigo">
              <feather-icon
                icon="MapIcon"
                size="20"
              />
            </div>
            <div class="detail-content">
              <div class="detail-label">
                Gouvernorat de destination
              </div>
              <div class="detail-value">
                {{ orderData.governorate.name }}
              </div>
            </div>
          </div>

          <div class="detail-card modern">
            <div class="detail-icon gradient-cyan">
              <feather-icon
                icon="NavigationIcon"
                size="20"
              />
            </div>
            <div class="detail-content">
              <div class="detail-label">
                Délégation de destination
              </div>
              <div class="detail-value">
                {{ orderData.delegation.name }}
              </div>
            </div>
          </div>

          <div class="detail-card modern">
            <div class="detail-icon gradient-emerald">
              <feather-icon
                icon="PackageIcon"
                size="20"
              />
            </div>
            <div class="detail-content">
              <div class="detail-label">
                Nombre d’articles
              </div>
              <div class="detail-value">
                {{ orderData.products.length }}
              </div>
            </div>
          </div>

          <div class="detail-card modern">
            <div class="detail-icon gradient-pink">
              <feather-icon
                icon="UserCheckIcon"
                size="20"
              />
            </div>
            <div class="detail-content">
              <div class="detail-label">
                Fournisseur expéditeur
              </div>
              <div class="detail-value">
                {{ orderData.supplier.username }}
              </div>
            </div>
          </div>

          <div
            v-if="orderData.note && orderData.note.trim()"
            class="detail-card modern note-card"
          >
            <div class="detail-icon gradient-amber">
              <feather-icon
                icon="FileTextIcon"
                size="20"
              />
            </div>
            <div class="detail-content">
              <div class="detail-label">
                Note
              </div>
              <div class="detail-value note-content">
                {{ orderData.note }}
              </div>
            </div>
          </div>
        </div>

        <!-- Client Information Grid -->
        <div class="details-grid-modern">
          <div class="info-card modern">
            <div class="info-icon gradient-blue">
              <feather-icon
                icon="UserIcon"
                size="24"
              />
            </div>
            <div class="info-content">
              <div class="info-label">
                Nom du destinataire
              </div>
              <div class="info-value">
                {{ orderData.name }}
              </div>
            </div>
          </div>

          <div class="info-card modern">
            <div class="info-icon gradient-green">
              <feather-icon
                icon="MapPinIcon"
                size="24"
              />
            </div>
            <div class="info-content">
              <div class="info-label">
                Adresse
              </div>
              <div class="info-value">
                {{ orderData.address }}
              </div>
            </div>
          </div>

          <div class="info-card modern">
            <div class="info-icon gradient-purple">
              <feather-icon
                icon="PhoneIcon"
                size="24"
              />
            </div>
            <div class="info-content">
              <div class="info-label">
                Téléphone
              </div>
              <div class="info-value">
                {{ orderData.phone1 }}
              </div>
            </div>
          </div>

          <div class="info-card modern">
            <div class="info-icon gradient-red">
              <feather-icon
                icon="DollarSignIcon"
                size="24"
              />
            </div>
            <div class="info-content">
              <div class="info-label">
                Montant
              </div>
              <div class="info-value">
                {{ orderData.price_ttc }} DT
              </div>
            </div>
          </div>

          <div class="info-card modern">
            <div class="info-icon gradient-teal">
              <feather-icon
                icon="CalendarIcon"
                size="24"
              />
            </div>
            <div class="info-content">
              <div class="info-label">
                Date d'ajout
              </div>
              <div class="info-value">
                {{ formatDate(orderData.date) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Products Section -->
        <div class="products-section">
          <div class="products-header">
            <feather-icon
              icon="ShoppingBagIcon"
              size="20"
            />
            <h3>Produits Commandés</h3>
          </div>
          <div class="products-grid">
            <div
              v-for="(product, index) in orderData.products"
              :key="index"
              class="product-card"
            >
              <div class="product-icon">
                <feather-icon
                  icon="BoxIcon"
                  size="18"
                />
              </div>
              <div class="product-info">
                <div class="product-name">
                  {{ product.product }}
                </div>
                <div class="product-quantity">
                  Quantité: {{ product.quantity }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

          </b-tab>

          <b-tab title="Historique du colis">
            <!-- History Section -->
            <div class="history-section">
        <div class="section-header">
          <div class="section-title">
            <feather-icon
              icon="ClockIcon"
              size="24"
            />
            <h2>Historique du Colis {{ orderData.id }}</h2>
          </div>
          <div class="history-count">
            <span class="count-badge">{{ importantHistories(orderData).length }} événements importants</span>
          </div>
        </div>

        <div
          v-if="importantHistories(orderData).length"
          class="timeline-modern"
        >
          <div
            v-for="(history, index) in importantHistories(orderData)"
            :key="history.id"
            class="timeline-item-modern"
            :class="{ 'timeline-item-last': index === importantHistories(orderData).length - 1 }"
          >
            <div class="timeline-connector" />
            <div
              class="timeline-icon-modern"
              :class="getHistoryIconClass(history.message)"
            >
              <feather-icon
                :icon="getHistoryIcon(history.message)"
                size="18"
              />
            </div>
            <div class="timeline-content-modern">
              <div class="timeline-header">
                <div class="timeline-message-modern">
                  {{ history.message }}
                </div>
                <div class="timeline-date-modern">
                  <feather-icon
                    icon="ClockIcon"
                    size="14"
                  />
                  {{ history.created_at_formatted }}
                </div>
              </div>
              <div class="timeline-description-modern">
                <div>{{ getMainDescription(history.description) }}</div>
                <div
                  v-if="getReturnReason(history.description)"
                  class="return-reason-badge"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                  />
                  <strong>Raison: {{ getReturnReason(history.description) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="history-empty"
        >
          <feather-icon
            icon="ClockIcon"
            size="24"
          />
          <span>Aucun événement important pour ce colis.</span>
        </div>
            </div>
          </b-tab>
        </b-tabs>
      </div>
    </div>

    <div
      v-if="!orderResults.length && !loading && !error"
      class="no-data"
    >
      <feather-icon
        icon="SearchIcon"
        size="64"
        class="no-data-icon"
      />
      <p>Entrez un numéro de colis ou un numéro de téléphone pour commencer la recherche</p>
    </div>

    <div
      v-if="loading"
      class="loading"
    >
      <b-spinner />
      <span>Recherche en cours...</span>
    </div>
  </div>
</template>

<script>
import {
  BFormInput,
  BButton,
  BAlert,
  BSpinner,
  BTabs,
  BTab,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  name: 'SearchHistory',
  components: {
    BFormInput,
    BButton,
    BAlert,
    BSpinner,
    BTabs,
    BTab,
    FeatherIcon,
  },
  data() {
    return {
      searchQuery: '',
      orderData: null,
      orderResults: [],
      searchMeta: null,
      loading: false,
      error: null,
      user: storeAuth.state.user || { id: '8033' },
    }
  },
  mounted() {
    // Load user data if available
    if (storeAuth.state.user) {
      this.user = storeAuth.state.user
    }
  },
  methods: {
    async searchOrder() {
      if (!this.searchQuery.trim()) {
        this.error = 'Veuillez entrer un numéro de colis'
        return
      }

      this.loading = true
      this.error = null
      this.orderData = null
      this.orderResults = []
      this.searchMeta = null

      try {
        const response = await axios.get(`/api/orders/search-history/${this.searchQuery}/`)

        if (response.data) {
          // Handle new API response format with results array
          if (response.data.results && response.data.results.length > 0) {
            const [firstOrder] = response.data.results
            this.orderData = firstOrder
            this.orderResults = response.data.results
            this.searchMeta = response.data.search_meta || null
          } else if (response.data.id) {
            // Handle legacy format (direct order object)
            this.orderData = response.data
            this.orderResults = [response.data]
          } else {
            this.error = 'Aucune commande trouvée'
            this.orderData = null
            this.orderResults = []
          }
        } else {
          this.error = 'Aucune commande trouvée'
          this.orderData = null
          this.orderResults = []
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.error = 'Aucune commande trouvée avec cet ID ou numéro de téléphone'
        } else if (error.response && error.response.status >= 500) {
          this.error = 'Erreur serveur. Veuillez réessayer plus tard.'
        } else if (error.request) {
          this.error = 'Erreur de connexion. Vérifiez votre connexion internet.'
        } else {
          this.error = 'Erreur lors de la recherche. Veuillez réessayer.'
        }
        this.orderData = null
        this.orderResults = []
        this.searchMeta = null
        // eslint-disable-next-line no-console
        console.error('Search error:', error)
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    },

    getMainDescription(description) {
      if (!description) return ''

      // Check if the description contains a return reason using more flexible patterns
      let reasonMatch = null

      // Pattern 1: "La raison est :" or "Le raison est :" (with typo versions)
      reasonMatch = description.match(/L[ae] r[ae]?sion est\s*:\s*(.+?)(?:\s*\.|$)/i)

      // Pattern 2: "La raison est:" (without space before colon)
      if (!reasonMatch) {
        reasonMatch = description.match(/L[ae] r[ae]?sion est:\s*(.+?)(?:\s*\.|$)/i)
      }

      // Pattern 3: More general pattern for "raison est"
      if (!reasonMatch) {
        reasonMatch = description.match(/raison est\s*:\s*(.+?)(?:\s*\.|$)/i)
      }

      if (reasonMatch) {
        // Return the description without the reason part
        return description.substring(0, reasonMatch.index).trim()
      }

      // Return the full description if no reason found
      return description
    },

    getReturnReason(description) {
      if (!description) return null

      // More flexible regex patterns to match various formats
      let reasonMatch = null

      // Pattern 1: "La raison est :" or "Le raison est :" (with typo versions)
      reasonMatch = description.match(/L[ae] r[ae]?sion est\s*:\s*(.+?)(?:\s*\.|$)/i)

      // Pattern 2: "La raison est:" (without space before colon)
      if (!reasonMatch) {
        reasonMatch = description.match(/L[ae] r[ae]?sion est:\s*(.+?)(?:\s*\.|$)/i)
      }

      // Pattern 3: More general pattern for "raison est"
      if (!reasonMatch) {
        reasonMatch = description.match(/raison est\s*:\s*(.+?)(?:\s*\.|$)/i)
      }

      if (reasonMatch && reasonMatch[1] && reasonMatch[1].trim() !== 'None' && reasonMatch[1].trim() !== '') {
        const reason = reasonMatch[1].trim()
        return reason
      }

      return null
    },

    importantHistories(order) {
      if (!order || !Array.isArray(order.histories)) return []

      return order.histories.filter(history => {
        const newStatus = this.normalizeStatusCode(history.new_status)
        const oldStatus = this.normalizeStatusCode(history.old_status)
        const message = (history.message || '').toLowerCase()
        const statusText = `${history.old_status_display || ''} ${history.new_status_display || ''}`.toLowerCase()

        if (message.includes('ticket') || message.includes('réclamation')) {
          return false
        }

        return (
          this.importantStatusCodes.includes(newStatus)
          || this.importantStatusCodes.includes(oldStatus)
          || this.importantStatusWords.some(word => message.includes(word) || statusText.includes(word))
        )
      })
    },

    normalizeStatusCode(status) {
      if (status === null || status === undefined || status === '') return null
      const code = Number(status)
      return Number.isNaN(code) ? null : code
    },

    getStatusVariant(status) {
      const statusMap = {
        0: 'secondary', // En attente
        1: 'info', // Au dépôt
        2: 'warning', // En cours de livraison
        3: 'success', // Livré
        4: 'warning', // Reporté
        5: 'danger', // Retour au dépôt
        6: 'success', // Livré payé
        7: 'danger', // Retour expéditeur
        22: 'danger', // Retour définitif
      }
      return statusMap[status] || 'secondary'
    },

    getStatusIcon(status) {
      const iconMap = {
        0: 'ClockIcon', // En attente
        1: 'PackageIcon', // Au dépôt
        2: 'TruckIcon', // En cours de livraison
        3: 'CheckCircleIcon', // Livré
        4: 'AlertCircleIcon', // Reporté
        5: 'RotateCcwIcon', // Retour au dépôt
        6: 'CheckSquareIcon', // Livré payé
        7: 'XCircleIcon', // Retour expéditeur
        22: 'XCircleIcon', // Retour définitif
      }
      return iconMap[status] || 'InfoIcon'
    },

    getHistoryIcon(message) {
      const messageKey = (message || '').toLowerCase()
      if (messageKey.includes('supprim')) {
        return 'TrashIcon'
      }
      if (messageKey.includes('livré') || messageKey.includes('delivered')) {
        return 'CheckCircleIcon'
      } if (messageKey.includes('retour') || messageKey.includes('return')) {
        return 'RotateCcwIcon'
      } if (messageKey.includes('en cours') || messageKey.includes('transit')) {
        return 'TruckIcon'
      } if (messageKey.includes('dépôt') || messageKey.includes('depot')) {
        return 'PackageIcon'
      } if (messageKey.includes('reporté') || messageKey.includes('delayed')) {
        return 'AlertCircleIcon'
      }
      return 'InfoIcon'
    },

    getHistoryIconClass(message) {
      const messageKey = (message || '').toLowerCase()
      if (messageKey.includes('supprim')) {
        return 'status-returned'
      }
      if (messageKey.includes('livré') || messageKey.includes('delivered')) {
        return 'status-delivered' // Green - Success
      } if (messageKey.includes('retour') || messageKey.includes('return')) {
        return 'status-returned' // Red - Danger
      } if (messageKey.includes('en cours') || messageKey.includes('transit')) {
        return 'status-in-transit' // Blue - In Progress
      } if (messageKey.includes('dépôt') || messageKey.includes('depot')) {
        return 'status-depot' // Orange - At Depot
      } if (messageKey.includes('reporté') || messageKey.includes('delayed')) {
        return 'status-delayed' // Yellow - Warning
      }
      return 'status-default' // Gray - Default
    },
  },
  computed: {
    importantStatusCodes() {
      return [0, 2, 3, 4, 5, 6, 7, 9, 22, 28]
    },
    importantStatusWords() {
      return [
        'en attente',
        'en cours',
        'dépôt',
        'depot',
        'retour au dépôt',
        'retour au depot',
        'retour définitif',
        'definitive return',
        'livré',
        'livre',
        'livré payé',
        'retour payé',
      ]
    },
  },
}
</script>

<style scoped>
.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
}

.title {
  font-size: 2.5em;
  font-weight: 700;
  background: linear-gradient(135deg, #6B46C1, #9333EA);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: flex;
  align-items: center;
  gap: 15px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #6B46C1;
  font-weight: 600;
}

.search-section {
  display: flex;
  justify-content: flex-start;
}

.search-container {
  display: flex;
  gap: 15px;
  width: 100%;
}

.search-input {
  flex: 1;
  padding: 15px 20px;
  border: 2px solid rgba(107, 114, 128, 0.2);
  border-radius: 15px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  border-color: #6B46C1;
  box-shadow: 0 0 0 0.2rem rgba(107, 70, 193, 0.15);
}

.search-btn {
  padding: 15px 30px;
  border-radius: 15px;
  font-weight: 600;
  display: flex;
  height: 70%;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #6B46C1, #9333EA);
  border: none;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background: linear-gradient(135deg, #9333EA, #A855F7);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(107, 70, 193, 0.25);
}

.main-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: fadeInUp 0.6s ease-out;
}

.results-summary {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding: 12px 18px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 10px;
  color: #9a3412;
  font-weight: 700;
}

.order-result {
  display: flex;
  flex-direction: column;
  padding-bottom: 10px;
}

.order-tabs {
  background: transparent;
}

.order-tabs ::v-deep .nav-tabs {
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 0 6px;
  border-bottom: 1px solid #e5e7eb;
}

.order-tabs ::v-deep .nav-tabs .nav-link {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 18px;
  border: 1px solid transparent;
  border-bottom: 3px solid transparent;
  border-radius: 10px 10px 0 0;
  color: #6b7280;
  font-weight: 700;
  transition: color 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}

.order-tabs ::v-deep .nav-tabs .nav-link:hover {
  color: #111827;
  background: #f9fafb;
}

.order-tabs ::v-deep .nav-tabs .nav-link.active {
  color: #059669;
  background: #ffffff;
  border-color: #d1fae5 #d1fae5 #059669;
}

.order-tabs-content {
  padding-top: 18px;
}

/* Section Styles */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  padding: 20px 0;
  border-bottom: 2px solid rgba(16, 185, 129, 0.1);
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title h2 {
  font-size: 1.8em;
  font-weight: 700;
  color: black;
  margin: 0;
}

.section-title .feather {
  color: #10B981;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

/* Order ID Display - Modern Glass Morphism */
.order-id-display {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.4));
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  box-shadow:
    0 8px 32px rgba(31, 38, 135, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;
  overflow: hidden;
}

.order-id-display::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(255, 159, 67, 0.1) 0%,
    rgba(255, 140, 0, 0.05) 50%,
    rgba(230, 126, 0, 0.08) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.order-id-display:hover::before {
  opacity: 1;
}

.order-id-display:hover {
  transform: translateY(-6px) scale(1.02);
  box-shadow:
    0 20px 60px rgba(31, 38, 135, 0.25),
    0 0 0 1px rgba(255, 159, 67, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6));
}

.order-id-display .feather {
  width: 28px;
  height: 28px;
  color: #ff9f43;
  filter: drop-shadow(0 4px 8px rgba(255, 159, 67, 0.2));
  transition: all 0.3s ease;
}

.order-id-display:hover .feather {
  transform: rotate(5deg) scale(1.1);
  color: #ff8c00;
}

.id-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.id-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #6B7280;
  margin-bottom: 0;
  opacity: 0.8;
  transition: color 0.3s ease;
}

.id-value {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 0.5px;
  color: #111827;
  transition: all 0.3s ease;
}

/* Client Information Section */
.client-info-section {
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.client-info-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #ff9f43, #ff8c00, #ffb976);
}

.client-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 5px; /* Spacing between cards */
  margin-bottom: 25px; /* Added margin to increase space between this grid and the next section */
}

.info-card.modern {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: linear-gradient(135deg, #ffffff, #f9fafb);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.info-card.modern::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--gradient);
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: left;
}

.info-card.modern:hover::before {
  transform: scaleX(1);
}

.info-card.modern:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
}

.info-icon.gradient-blue {
  background: linear-gradient(135deg, #3B82F6, #1D4ED8);
  --gradient: linear-gradient(90deg, #3B82F6, #1D4ED8);
}

.info-icon.gradient-green {
  background: linear-gradient(135deg, #10B981, #047857);
  --gradient: linear-gradient(90deg, #10B981, #047857);
}

.info-icon.gradient-purple {
  background: linear-gradient(135deg, #8B5CF6, #7C3AED);
  --gradient: linear-gradient(90deg, #8B5CF6, #7C3AED);
}

.info-icon.gradient-orange {
  background: linear-gradient(135deg, #F59E0B, #D97706);
  --gradient: linear-gradient(90deg, #F59E0B, #D97706);
}

.info-icon.gradient-red {
  background: linear-gradient(135deg, #ff9f43, #ff8c00);
  --gradient: linear-gradient(90deg, #ff9f43, #ff8c00);
}

.info-icon.gradient-teal {
  background: linear-gradient(135deg, #14B8A6, #0D9488);
  --gradient: linear-gradient(90deg, #14B8A6, #0D9488);
}

.info-icon.gradient-status {
  background: linear-gradient(135deg, #ff9f43, #ff8c00);
  --gradient: linear-gradient(90deg, #ff9f43, #ff8c00);
}

.info-icon.gradient-bl {
  background: linear-gradient(135deg, #6366F1, #4F46E5);
  --gradient: linear-gradient(90deg, #6366F1, #4F46E5);
}

.info-card.status-card {
  border: 2px solid #ff9f43;
  background: linear-gradient(135deg, #ffffff, #fff7ed);
}

.info-card.status-card:hover {
  border-color: #ff8c00;
  background: linear-gradient(135deg, #fff7ed, #ffedd5);
}

.info-card.bl-card {
  border: 2px solid #6366F1;
  background: linear-gradient(135deg, #ffffff, #f0f4ff);
}

.info-card.bl-card:hover {
  border-color: #4F46E5;
  background: linear-gradient(135deg, #f0f4ff, #e0e7ff);
}

.info-card.modern .info-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.info-card.modern .info-content {
  flex: 1;
}

.info-card.modern .info-label {
  font-size: 12px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.info-card.modern .info-value {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
}

/* Order Details Section */
.order-details-section {
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.order-details-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #F59E0B, #ff9f43, #ffb976);
}

.details-grid-modern {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
}

.detail-card.modern {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: linear-gradient(135deg, #ffffff, #f9fafb);
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.detail-card.modern::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--gradient);
  transform: scaleY(0);
  transition: transform 0.3s ease;
  transform-origin: bottom;
}

.detail-card.modern:hover::before {
  transform: scaleY(1);
}

.detail-card.modern:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}

.detail-icon.gradient-indigo {
  background: linear-gradient(135deg, #6366F1, #4F46E5);
  --gradient: linear-gradient(135deg, #6366F1, #4F46E5);
}

.detail-icon.gradient-cyan {
  background: linear-gradient(135deg, #06B6D4, #0891B2);
  --gradient: linear-gradient(135deg, #06B6D4, #0891B2);
}

.detail-icon.gradient-emerald {
  background: linear-gradient(135deg, #10B981, #059669);
  --gradient: linear-gradient(135deg, #10B981, #059669);
}

.detail-icon.gradient-pink {
  background: linear-gradient(135deg, #EC4899, #DB2777);
  --gradient: linear-gradient(135deg, #EC4899, #DB2777);
}

.detail-icon.gradient-amber {
  background: linear-gradient(135deg, #F59E0B, #D97706);
  --gradient: linear-gradient(135deg, #F59E0B, #D97706);
}

.detail-card.modern .detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.detail-card.modern .detail-content {
  flex: 1;
}

.detail-card.modern .detail-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.detail-card.modern .detail-value {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

/* Note card special styling */
.detail-card.note-card {
  grid-column: 1 / -1; /* Full width across all columns */
  background: linear-gradient(135deg, #FEF3C7, #FDE68A);
  border: 2px solid #F59E0B;
}

.detail-card.note-card:hover {
  background: linear-gradient(135deg, #FDE68A, #FCD34D);
  border-color: #D97706;
}

.detail-card.note-card .note-content {
  font-style: italic;
  word-wrap: break-word;
  max-width: 100%;
  white-space: pre-wrap;
}

/* Products Section */
.products-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 2px solid rgba(16, 185, 129, 0.1);
}

.products-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.products-header h3 {
  font-size: 1.4em;
  font-weight: 700;
  color: #374151;
  margin: 0;
}

.products-header .feather {
  color: #10B981;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 15px;
}

.product-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
  border-radius: 10px;
  border: 1px solid rgba(16, 185, 129, 0.2);
  transition: all 0.3s ease;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.15);
  border-color: #10B981;
}

.product-icon {
  width: 35px;
  height: 35px;
  background: linear-gradient(135deg, #10B981, #059669);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.product-info {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
  font-size: 14px;
}

.product-quantity {
  font-size: 12px;
  color: #059669;
  font-weight: 500;
}

/* History Section */
.history-section {
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.history-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(90deg, #8B5CF6, #ff9f43, #F59E0B);
}

.history-count {
  display: flex;
  align-items: center;
}

.count-badge {
  background: linear-gradient(135deg, #ffb976, #ff9f43);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 159, 67, 0.3);
}

.timeline-modern {
  position: relative;
  padding: 8px 0 0;
}

.timeline-item-modern {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff, #f9fafb);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
}

.timeline-item-modern:hover {
  transform: translateX(10px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  background: linear-gradient(135deg, #ffffff, #f0fdf4);
}

.timeline-item-modern:last-child {
  margin-bottom: 0;
}

.timeline-connector {
  position: absolute;
  left: 35px;
  top: 60px;
  bottom: -20px;
  width: 2px;
  background: linear-gradient(180deg, #10B981, #059669);
  opacity: 0.3;
}

.timeline-item-last .timeline-connector {
  display: none;
}

.timeline-icon-modern {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6B7280, #4B5563);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 6px 15px rgba(107, 114, 128, 0.25);
  border: 2px solid white;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
}

/* Status-specific icon colors - Default state */
.timeline-icon-modern.status-delivered {
  background: linear-gradient(135deg, #10B981, #059669);
  box-shadow: 0 6px 15px rgba(16, 185, 129, 0.35);
}

.timeline-icon-modern.status-returned {
  background: linear-gradient(135deg, #ff9f43, #ff8c00);
  box-shadow: 0 6px 15px rgba(255, 159, 67, 0.35);
}

.timeline-icon-modern.status-in-transit {
  background: linear-gradient(135deg, #3B82F6, #1D4ED8);
  box-shadow: 0 6px 15px rgba(59, 130, 246, 0.35);
}

.timeline-icon-modern.status-depot {
  background: linear-gradient(135deg, #F59E0B, #D97706);
  box-shadow: 0 6px 15px rgba(245, 158, 11, 0.35);
}

.timeline-icon-modern.status-delayed {
  background: linear-gradient(135deg, #EAB308, #CA8A04);
  box-shadow: 0 6px 15px rgba(234, 179, 8, 0.35);
}

.timeline-icon-modern.status-default {
  background: linear-gradient(135deg, #6B7280, #4B5563);
  box-shadow: 0 6px 15px rgba(107, 114, 128, 0.25);
}

/* Hover effects for timeline icons */
.timeline-icon-modern:hover {
  transform: scale(1.1);
}

.timeline-icon-modern.status-delivered:hover {
  background: linear-gradient(135deg, #059669, #047857);
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.45);
}

.timeline-icon-modern.status-returned:hover {
  background: linear-gradient(135deg, #ff8c00, #e67e00);
  box-shadow: 0 8px 20px rgba(255, 159, 67, 0.45);
}

.timeline-icon-modern.status-in-transit:hover {
  background: linear-gradient(135deg, #1D4ED8, #1E3A8A);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.45);
}

.timeline-icon-modern.status-depot:hover {
  background: linear-gradient(135deg, #D97706, #B45309);
  box-shadow: 0 8px 20px rgba(245, 158, 11, 0.45);
}

.timeline-icon-modern.status-delayed:hover {
  background: linear-gradient(135deg, #CA8A04, #A16207);
  box-shadow: 0 8px 20px rgba(234, 179, 8, 0.45);
}

.timeline-icon-modern.status-default:hover {
  background: linear-gradient(135deg, #4B5563, #374151);
  box-shadow: 0 8px 20px rgba(107, 114, 128, 0.35);
}

.timeline-content-modern {
  flex: 1;
  padding-top: 5px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 15px;
}

.timeline-message-modern {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
  flex: 1;
}

.timeline-date-modern {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #10B981;
  font-weight: 600;
  background: rgba(16, 185, 129, 0.1);
  padding: 6px 12px;
  border-radius: 20px;
  white-space: nowrap;
}

.timeline-description-modern {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.5;
  margin-top: 6px;
}

.history-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 130px;
  padding: 24px;
  color: #6b7280;
  font-weight: 600;
  text-align: center;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
}

.history-empty .feather {
  color: #10b981;
}

/* Return reason badge styling */
.return-reason-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #FFF7ED, #FFEDD5);
  padding: 12px 16px;
  border-radius: 12px;
  border-left: 4px solid #ff9f43;
  margin: 12px 0 0 0;
  box-shadow: 0 4px 12px rgba(255, 159, 67, 0.15);
  transition: all 0.3s ease;
  font-size: 14px;
}

.return-reason-badge strong {
  color: #ff9f43;
  font-weight: 700;
  flex: 1;
}

.return-reason-badge .feather {
  color: #ff9f43;
  flex-shrink: 0;
}

.return-reason-badge:hover {
  background: linear-gradient(135deg, #FFEDD5, #FED7AA);
  box-shadow: 0 6px 20px rgba(255, 159, 67, 0.25);
  transform: translateY(-2px);
}

/* Legacy return reason styling (for backward compatibility) */
.timeline-description-modern strong[style*="color: #ff9f43"] {
  display: inline-block;
  background: linear-gradient(135deg, #FFF7ED, #FFEDD5);
  padding: 8px 12px;
  border-radius: 8px;
  border-left: 4px solid #ff9f43;
  margin: 8px 0;
  box-shadow: 0 2px 8px rgba(255, 159, 67, 0.1);
  transition: all 0.3s ease;
}

.timeline-description-modern strong[style*="color: #ff9f43"]:hover {
  background: linear-gradient(135deg, #FFEDD5, #FED7AA);
  box-shadow: 0 4px 12px rgba(255, 159, 67, 0.15);
  transform: translateY(-1px);
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 50px;
  font-size: 18px;
  color: #10B981;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 20px 0;
}

.no-data {
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 18px;
}

.no-data-icon {
  color: #ccc;
  margin-bottom: 20px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@media (max-width: 1024px) {
  .client-info-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .details-grid-modern {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .timeline-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .timeline-date-modern {
    align-self: flex-start;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 15px;
  }

  .header {
    padding: 20px;
  }

  .title {
    font-size: 1.8em;
  }

  .search-container {
    flex-direction: column;
  }

  .search-input, .search-btn {
    width: 100%;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .header-right {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }

  .code-no-inline {
    align-items: flex-start;
    padding: 14px 16px;
  }

  .code-value {
    font-size: 18px;
  }

  .status-badge-large {
    padding: 14px 20px;
    font-size: 12px;
    gap: 8px;
  }

  .section-title h2 {
    font-size: 1.5em;
  }

  .client-info-grid {
    grid-template-columns: 1fr;
  }

  .details-grid-modern {
    grid-template-columns: 1fr;
  }

  .info-card.modern,
  .detail-card.modern {
    padding: 15px;
  }

  .timeline-item-modern {
    padding: 15px;
    gap: 12px;
  }

  .timeline-item-modern:hover {
    transform: translateX(5px);
  }

  .timeline-connector {
    left: 25px;
  }

  .timeline-icon-modern {
    width: 35px;
    height: 35px;
  }

  .client-info-section,
  .order-details-section,
  .history-section {
    padding: 20px;
  }

  .order-tabs-content {
    padding-top: 12px;
  }

  .order-tabs ::v-deep .nav-tabs {
    padding: 0;
  }

  .order-tabs ::v-deep .nav-tabs .nav-link {
    flex: 1;
    justify-content: center;
    padding: 10px 8px;
    font-size: 13px;
  }
}
</style>
